import { useEffect, useState } from "react";

const CULL_TOTAL_MS = 10000; // round length, for the countdown bar

// Slice colours, in list order. Stranger marks always render red.
const SLICE = ["#78ebff", "#f0d060", "#9d7dff", "#5ce6a5", "#ff9f5c", "#7fa8ff"];
const colorFor = (o, i) => (o.by_stranger ? "#ff5555" : SLICE[i % SLICE.length]);

/** Donut chart of the current tally. Empty ring when nobody has voted yet. */
const VotePie = ({ options, total }) => {
  const R = 34, C = 40, STROKE = 13, circ = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg className="cull-pie" viewBox="0 0 80 80" width="80" height="80">
      <circle cx={C} cy={C} r={R} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={STROKE} />
      {total > 0 && options.map((o, i) => {
        const frac = (o.votes || 0) / total;
        if (frac <= 0) return null;
        const len = frac * circ;
        const el = (
          <circle
            key={o.id}
            cx={C} cy={C} r={R}
            fill="none"
            stroke={colorFor(o, i)}
            strokeWidth={STROKE}
            strokeDasharray={`${len} ${circ - len}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${C} ${C})`}
          />
        );
        offset += len;
        return el;
      })}
      <text x={C} y={C + 4} textAnchor="middle" className="cull-pie-total">{total}</text>
    </svg>
  );
};

/**
 * Knockout vote to thin out the markers. Rounds repeat until one survives.
 * Clicking a row's NAME flies the map to that marker; the VOTE OUT button
 * casts the actual vote.
 */
export const CullVote = ({ vote, votedId, onVote, onLocate }) => {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, (vote.deadline_ts * 1000) - Date.now()));
    tick();
    const id = setInterval(tick, 150);
    return () => clearInterval(id);
  }, [vote.deadline_ts]);

  if (vote.finished) {
    return (
      <div className="cull-overlay" data-testid="cull-vote">
        <div className="cull-box">
          <div className="cull-result">
            {vote.dropped_name
              ? <><strong>{vote.dropped_name}</strong> was removed. One marker remains.</>
              : <>One marker remains.</>}
          </div>
        </div>
      </div>
    );
  }

  if (vote.resolved) {
    return (
      <div className="cull-overlay" data-testid="cull-vote">
        <div className="cull-box">
          <div className="cull-round">ROUND {vote.round}</div>
          <div className="cull-result">
            {vote.dropped_name
              ? <><strong>{vote.dropped_name}</strong> lost the vote and was removed.</>
              : <>A marker was removed.</>}
          </div>
          <div className="cull-sub">next round starting…</div>
        </div>
      </div>
    );
  }

  const secs = Math.ceil(remaining / 1000);
  const pct = Math.max(0, Math.min(100, (remaining / CULL_TOTAL_MS) * 100));
  const options = (vote.options || []).slice().sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const total = options.reduce((s, o) => s + (o.votes || 0), 0);

  return (
    <div className="cull-overlay" data-testid="cull-vote">
      <div className="cull-box">
        <div className="cull-topline">
          <span className="cull-round">ROUND {vote.round}</span>
          {votedId && <span className="cull-voted-badge">✓ VOTE CAST</span>}
        </div>

        <div className="cull-head">Multiple markers placed. Choose one to remove.</div>
        <div className="cull-sub">The one with the most votes will be lost.</div>

        <div className="cull-mid">
          <VotePie options={options} total={total} />
          <div className="cull-mid-right">
            <div className="cull-timer-bar"><div className="cull-timer-fill" style={{ width: `${pct}%` }} /></div>
            <div className="cull-timer-secs">{secs}s</div>
          </div>
        </div>

        <div className="cull-list">
          {options.map((o, i) => (
            <div key={o.id} className={`cull-opt ${votedId === o.id ? "voted" : ""}`}>
              <span className="cull-swatch" style={{ background: colorFor(o, i) }} />
              <button
                className="cull-opt-name"
                onClick={() => onLocate && onLocate(o)}
                title="Zoom to this marker"
                data-testid={`cull-locate-${o.id}`}
              >
                {o.name}
                <span className="cull-opt-region">{o.by_stranger ? "unknown signal" : o.region}</span>
              </button>
              <span className="cull-opt-votes">{o.votes || 0}</span>
              <button
                className="cull-opt-btn"
                onClick={() => !votedId && onVote(o.id)}
                disabled={!!votedId}
                data-testid={`cull-vote-${o.id}`}
              >
                {votedId === o.id ? "VOTED" : "VOTE OUT"}
              </button>
            </div>
          ))}
        </div>

        <div className="cull-foot">
          {votedId ? "Your vote is counted" : "Tap a name to view it on the map"}
        </div>
      </div>
    </div>
  );
};
